import { CategoryAxis, ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { SvgLegend } from "@/components/tool-kit/charts/legend";
import { px } from "@/components/tool-kit/charts/marks";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  tickFormat,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_PADDING,
  CHART_WIDTH,
  cartesianFrame,
  heightForPlot,
  layoutLegend,
} from "@/lib/tool-kit/charts/layout";
import { bandScale, linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { maxTextWidth } from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A population pyramid: age groups stacked youngest at the foot, males drawn to the left of
 * a centre line and females to the right, on one symmetric axis so the two sides compare
 * directly. A second population can be laid over the first as an outline.
 */

export interface PyramidSeries {
  male: number[];
  female: number[];
}

const ROW_HEIGHT = 18;
const MIN_PLOT_HEIGHT = 200;
const BAND_PADDING = { paddingInner: 0.12, paddingOuter: 0 };
const TICK_FONT = { fontSize: CHART_FONT_SIZE.tick };
const OUTLINE_WIDTH = 2;

/** Ticks from −max to +max on one scale, with the 0 in the middle. */
function symmetricTicks(max: number): {
  ticks: number[];
  limit: number;
  decimals: number;
} {
  const axis = niceTicks(0, max > 0 ? max : 1, { count: 4 });
  const positive = axis.ticks.filter((tick) => tick > 0);
  return {
    ticks: [...positive.map((tick) => -tick).reverse(), 0, ...positive],
    limit: axis.max,
    decimals: axis.decimals,
  };
}

export function PyramidChart({
  id,
  labels,
  primary,
  comparison,
  maleName,
  femaleName,
  comparisonName,
  xTitle,
  yTitle,
  style,
  fmt,
  label,
  description,
}: {
  id: string;
  /** Age groups, youngest first. */
  labels: string[];
  primary: PyramidSeries;
  comparison: PyramidSeries | null;
  maleName: string;
  femaleName: string;
  /** Legend entry for the outline; used only with `comparison`. */
  comparisonName: string;
  xTitle: string;
  yTitle: string;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
}) {
  const drawn = comparison ? [primary, comparison] : [primary];
  const largest = Math.max(
    0,
    ...drawn.flatMap((series) => [...series.male, ...series.female])
  );
  const { ticks, limit, decimals } = symmetricTicks(largest);
  const tick = tickFormat(fmt, decimals);
  const legend = layoutLegend(
    [maleName, femaleName, ...(comparison ? [comparisonName] : [])],
    CHART_WIDTH - 2 * CHART_PADDING
  );
  const input = {
    width: CHART_WIDTH,
    title: style.apa ? "" : style.title,
    xTitle,
    yTitle,
    legendHeight: legend.height,
    leftLabelWidth: maxTextWidth(labels, TICK_FONT),
    bottomLabelHeight: CHART_FONT_SIZE.tick,
    topPadding: 8,
  };
  const height = heightForPlot(
    Math.max(MIN_PLOT_HEIGHT, labels.length * ROW_HEIGHT),
    input
  );
  const frame = cartesianFrame({ ...input, height });
  const { plot } = frame;
  const x = linearScale([-limit, limit], [plot.x, plot.x + plot.width]);
  const band = bandScale(
    labels.length,
    [plot.y, plot.y + plot.height],
    BAND_PADDING
  );
  const rowOf = (group: number) => labels.length - 1 - group;
  const start = colorIndex(style.color);
  const maleColor = paletteColor(start, 0);
  const femaleColor = paletteColor(start, 1);
  const centre = x(0);
  const valueSize = CHART_FONT_SIZE.value;
  const showValues = style.valueLabels;
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      <SvgLegend
        keys={[
          { color: maleColor, kind: "swatch" },
          { color: femaleColor, kind: "swatch" },
          ...(comparison
            ? [{ color: CHART_INK.text, kind: "line" as const }]
            : []),
        ]}
        layout={legend}
        x={CHART_PADDING}
        y={frame.legendTop}
      />
      <ValueAxis
        apa={style.apa}
        format={(value) => tick(Math.abs(value))}
        orient="bottom"
        plot={plot}
        scale={x}
        ticks={ticks}
        zeroLine
      />
      <CategoryAxis
        apa={style.apa}
        axisLine
        labels={{
          rotated: false,
          lines: labels.map((text) => [text]),
          height: 0,
        }}
        orient="left"
        plot={plot}
        positions={labels.map((_, group) => band.center(rowOf(group)))}
      />
      {labels.map((text, group) => {
        const top = band.start(rowOf(group));
        const male = primary.male[group] ?? 0;
        const female = primary.female[group] ?? 0;
        return (
          <g key={text}>
            <rect
              fill={maleColor}
              height={px(band.bandwidth)}
              width={px(centre - x(-male))}
              x={px(x(-male))}
              y={px(top)}
            />
            <rect
              fill={femaleColor}
              height={px(band.bandwidth)}
              width={px(x(female) - centre)}
              x={px(centre)}
              y={px(top)}
            />
            {showValues ? (
              <>
                <text
                  fill={CHART_INK.text}
                  fontSize={valueSize}
                  textAnchor="end"
                  x={px(x(-male) - 3)}
                  y={px(top + band.bandwidth / 2 + valueSize * 0.35)}
                >
                  {valueText(fmt, male)}
                </text>
                <text
                  fill={CHART_INK.text}
                  fontSize={valueSize}
                  textAnchor="start"
                  x={px(x(female) + 3)}
                  y={px(top + band.bandwidth / 2 + valueSize * 0.35)}
                >
                  {valueText(fmt, female)}
                </text>
              </>
            ) : null}
          </g>
        );
      })}
      {comparison
        ? labels.map((text, group) => {
            const top = band.start(rowOf(group));
            const male = comparison.male[group] ?? 0;
            const female = comparison.female[group] ?? 0;
            return (
              <g key={`outline-${text}`}>
                <rect
                  fill="none"
                  height={px(band.bandwidth)}
                  stroke={CHART_INK.text}
                  strokeWidth={OUTLINE_WIDTH}
                  width={px(centre - x(-male))}
                  x={px(x(-male))}
                  y={px(top)}
                />
                <rect
                  fill="none"
                  height={px(band.bandwidth)}
                  stroke={CHART_INK.text}
                  strokeWidth={OUTLINE_WIDTH}
                  width={px(x(female) - centre)}
                  x={px(centre)}
                  y={px(top)}
                />
              </g>
            );
          })
        : null}
    </ChartSvg>
  );
}
